"use client";

import { canEncodeAudio } from "mediabunny";

import { audioBufferToOpus } from "@/utils/audio/audioBufferToOpus";
import { audioBufferToWav } from "@/utils/audio/audioBufferToWav";
import { AudioTargetFormat } from "@/utils/audio/types";
import { getFilename } from "@/utils/filenameExtension/getFilename";

// Encodes a decoded buffer with the best codec this browser supports. Opus needs the WebCodecs AudioEncoder, which older Safari/iOS lack; PCM WAV needs no encoder, so it is the fallback everywhere. Both branches resample to the same target so a caller gets the same audio either way, only the size differs.
export const encodeToOpusOrWavFile = async ({
  audioBuffer,
  baseFileName,
  targetFormat,
  bitrate,
}: {
  audioBuffer: AudioBuffer;
  baseFileName: string;
  targetFormat: AudioTargetFormat;
  bitrate: number;
}): Promise<File> => {
  // The capability probe needs concrete numbers; a null target means the buffer's own
  const { sampleRate, numberOfChannels } = targetFormat ?? audioBuffer;
  const canEncodeOpus = await canEncodeAudio("opus", {
    numberOfChannels,
    sampleRate,
    bitrate,
  });

  const outputBaseName = getFilename(baseFileName);

  if (canEncodeOpus) {
    const opusBlob = await audioBufferToOpus({
      audioBuffer,
      targetFormat,
      bitrate,
    });
    return new File([opusBlob], `${outputBaseName}.ogg`, { type: "audio/ogg" });
  }

  const wavBlob = await audioBufferToWav({ audioBuffer, targetFormat });
  return new File([wavBlob], `${outputBaseName}.wav`, { type: "audio/wav" });
};
